import { startCompletion } from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import type { EditorSelection, TransactionSpec } from '@codemirror/state';
import { EditorState, Transaction } from '@codemirror/state';
import {
  EditorView,
  placeholder as placeholderExtension,
} from '@codemirror/view';
import clsx from 'clsx';
import { AnimatePresence } from 'framer-motion';
import { isEqual } from 'lodash-es';
import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { FilterIcon } from '@dynatrace/strato-icons';

import { ErrorFallback } from '../../core/components/error-fallback/ErrorFallback.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { OverlayStackingContext } from '../../core/contexts/OverlayStacking.js';
import { useAriaLabelingProps as _useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { createStratoLogger } from '../../core/sdk/sdk-logger.js';
import { isSlot } from '../../core/utils/is-slot.js';
import * as editorStyles from '../../editors/base-code-editor/BaseCodeEditor.sty.js';
import { useInternalFormRegistration } from '../../forms/form/hooks/useInternalFormRegistration.js';
import { InternalBaseItem as BaseItem } from '../../forms/forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { withFormFieldMessageContextProvider } from '../../forms/forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { useFormControl } from '../../forms/forms-core/hooks/useFormControl.js';
import { getI18nValidationMessage } from '../../forms/forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../../forms/forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../../forms/hooks/useContainerFocus.js';
import type { FormControlWithOverlayRef } from '../../forms/shared-types.js';
import {
  NativeValidityStateKeys,
  StratoValidityStateKeys,
  ValidationError,
  type ValidationState,
  type ValidationTrigger,
} from '../../forms/validation/types.js';
import { useCustomMessageEmitter } from '../../forms/validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../../forms/validation/useValidationStateEmitter.js';
import { getValidationState } from '../../forms/validation/validation-builders.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { closeAllOverlays } from '../../overlays/hooks/useOverlay.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { FilterFieldSuffixButtons } from './components/FilterFieldSuffixButtons.js';
import { FilterFieldContext } from './contexts/FilterFieldContext.js';
import type { FilterFieldContextProps } from './contexts/FilterFieldContext.types.js';
import { FilterFieldLoggerContext } from './contexts/FilterFieldLoggerContext.js';
import { handleEditMode } from './editor-state-configs/edit-mode-update-listener.js';
import {
  ANNOTATION_COMPOSE_END,
  ANNOTATION_DELETE_STATEMENT,
  ANNOTATION_DOC_CHANGED_CONTROLLED,
  ANNOTATION_END_PASTING_MODE,
  ANNOTATION_SUGGESTION_APPLY,
  disabledPropCompartment,
  overrideKeys,
  pastingEffect,
  placeholderCompartment,
  readOnlyPropCompartment,
  stringAnnotation,
  suggestionsCompartment,
  validatorMapEffect,
  type PastingInfo,
} from './editor-state-configs/editor-config.js';
import {
  handleControlledPasting,
  handlePastingTransaction,
} from './editor-state-configs/pasting-update-listener.js';
import { obfuscateFilterString } from './error-fallback/filter-value-obfuscation-util.js';
import { DynamicFilterFieldConfigurations } from './extensions/compartments.js';
import { FilterFieldInteractionsExtension } from './extensions/interactions-extensions.js';
import { FilterFieldLanguageExtension } from './extensions/language-extension.js';
import { FilterFieldRecentSavedFiltersExtension } from './extensions/recent-saved-filters.js';
import { createFilterFieldSyntaxHighlightingExtension } from './extensions/syntax-highlighting-extension.js';
import * as styles from './FilterField.sty.js';
import type { FilterFieldProps } from './FilterField.types.js';
import { usePinnedFilterSuggestions } from './hooks/recent-and-pinned/usePinnedFilterSuggestions.js';
import { useRecentFilterMutation } from './hooks/recent-and-pinned/useRecentFilters.js';
import { useRecentFilterSuggestions } from './hooks/recent-and-pinned/useRecentFilterSuggestions.js';
import { useCachedTreeConversion } from './hooks/useCachedTreeConversion.js';
import { useErrorAwareViewRef } from './hooks/useErrorAwareViewRef.js';
import { useErrorLogsStore } from './hooks/useErrorLogsStore.js';
import { useFilterFieldLinting } from './hooks/useFilterFieldLinting.js';
import { useFilterFieldLoggerContext } from './hooks/useFilterFieldLoggerContext.js';
import { useSuggestions } from './hooks/useSuggestions.js';
import { useUpdatableCallback } from './hooks/useUpdatableCallback.js';
import { filterFieldMessages } from './messages.js';
import { getNumberParser } from './parser/getNumberParser.js';
import { filterFieldReducer } from './state/filter-field-state-reducer.js';
import { Suggestion } from './suggestions/Suggestion.js';
import { SuggestionDetails } from './suggestions/SuggestionDetails.js';
import { SuggestionGroup } from './suggestions/SuggestionGroup.js';
import { SuggestionGroupLabel } from './suggestions/SuggestionGroupLabel.js';
import { DEFAULT_SUGGESTIONS_COUNT } from './suggestions/suggestions-config.js';
import { suggestionsExtension } from './suggestions/suggestions-extension.js';
import { Suggestions } from './suggestions/Suggestions.js';
import { SuggestionsOverlay } from './suggestions/SuggestionsOverlay.js';
import { autoFormatLogicalOperators } from './utils/auto-format-logical-operators.js';
import { computeMinimalDocChange } from './utils/compute-minimal-doc-change.js';
import {
  getCurrentFocusableListItem,
  getFirstFocusableListItem,
  getLastFocusableListItem,
  getNextFocusableListItem,
} from './utils/get-next-suggestion.js';
import { flattenSuggestions } from './utils/suggestions-utils/flatten-suggestions.js';
import {
  type InternalValidatorMap,
  toInternalValidatorMap,
} from './utils/validation-utils/normalize-validator-map.js';
import { wrapEditorCoordsMethods } from './utils/wrap-editor-coords-methods.js';

// TODO: Keyboard navigation in filter field (easy deletion of whole statements etc.).
// TODO: Control label?
// TODO: Check keyboard navigation with empty suggestions overlay again.
// TODO: Test that custom suggestions are shown properly depending on the autoSuggestions prop.
// TODO: Check if we can combine tree conversion and error / diagnostics generation.
// TODO: Suggestions overlay max-height + virtualization.
// TODO: Add test for auto-formatting.
// TODO: Test for correct cursor position when editing in combination with auto-formatting of logical operators.

const COMPONENT_NAME = 'FilterField';

// Stable identity, so consumers memoizing on the flattened suggestions don't
// re-render on every pass while the overlay is closed.
const EMPTY_FLATTENED_SUGGESTIONS: ReturnType<typeof flattenSuggestions> = {
  flattenedSuggestionItems: [],
  groupMetadata: [],
};

function isEditorSelection(selection: unknown): selection is EditorSelection {
  return (selection as EditorSelection)?.ranges !== undefined;
}

/** @public */
export type FilterFieldRef = FormControlWithOverlayRef & {
  /**
   * Allows a recent filter to be saved into the users application state.
   */
  saveRecentFilter: () => Promise<void>;
};

const _FilterField = forwardRef<FilterFieldRef, FilterFieldProps>(
  (props, forwardedRef) => {
    const {
      children,
      name,
      value,
      defaultValue,
      required: propRequired,
      disabled: propDisabled,
      readOnly: propReadOnly = false,
      placeholder: placeholderProp,
      id: propId,
      _form,
      _messageTarget,
      autoSuggestions = false,
      defaultSuggestionsCount: _defaultSuggestionsCount,
      // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
      controlState,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-disabled': propAriaDisabled,
      onChange,
      onSuggest,
      onFilter,
      validatorMap,
      parserConfig,
      filterNamespace,
      customTypes,
      onValidityChange,
      onFocus,
      onBlur,
      ...remainingProps
    } = props;
    const [behavioralTrackingProps] =
      useBehavioralTrackingProps(remainingProps);

    const setCursorPosition = useErrorLogsStore(
      (state) => state.setCursorPosition,
    );
    const setAction = useErrorLogsStore((state) => state.setAction);
    const setIsControlled = useErrorLogsStore((state) => state.setIsControlled);

    const suggestionsSlot = Children.toArray(children).find((child) =>
      isSlot(child, Suggestions),
    );

    const { getSyntaxTree } = useCachedTreeConversion();

    const intl = useIntl();
    // Live validity is fanned out through the form registration below, which
    // forwards to both the consumer's `onValidityChange` and the enclosing
    // `_Form` runtime. Set once the registration hook has run.
    const formValidityChangeRef = useRef<
      ((state: ValidationState) => void) | undefined
    >(undefined);
    const _emitValidationState = useValidationStateEmitter(
      controlState
        ? undefined
        : (state) => formValidityChangeRef.current?.(state),
    );

    const { showBoundary } = useErrorBoundary();
    const { logger } = useFilterFieldLoggerContext();

    const onChangeRef = useUpdatableCallback(onChange);
    const onFilterRef = useUpdatableCallback(onFilter);
    // Whether the consumer supplied a handler at all. `onFilterRef` cannot
    // answer that: `useUpdatableCallback` always stores its error-boundary
    // wrapper, so its `current` is a function even when `onFilter` is not set.
    const hasFilterHandlerRef = useRef(onFilter !== undefined);
    hasFilterHandlerRef.current = onFilter !== undefined;

    // validatorMap ref to use in everything codemirror related.
    const validatorMapRef = useRef(validatorMap);
    const customTypesRef = useRef(customTypes);
    const internalValidatorMapRef = useRef<InternalValidatorMap | undefined>(
      undefined,
    );

    const numberParser = getNumberParser();

    const isUserEventComposeRef = useRef(false);
    const valuePlaceholderWildcardSelection = useRef<{
      isActive: boolean;
      range?: { from: number; to: number };
      potentialNewValue?: string;
    }>({ isActive: false });

    // Set by the editor's linting. Feeds the after-blur error gating as a
    // control-local error that does not live in the message context.
    const [hasSyntaxError, setHasSyntaxError] = useState(false);

    const rootId = useId();
    const {
      id,
      disabled,
      required,
      ariaDisabled,
      readOnly: _readOnly,
      hint,
      showError,
      inputProps,
      hintProps,
      messageDispatch: formFieldMessageContextDispatch,
      focusHandlers,
      markTouched,
    } = useFormControl({
      propAriaDisabled,
      propDisabled,
      propId,
      propRequired,
      propReadOnly,
      controlState,
      validationVisibility: 'after-blur',
      hasValidationError: hasSyntaxError,
    });
    const readOnly = !!_readOnly;

    // Values from previous render-cycle to trigger updates while rendering.
    // This way, we can avoid using effects, which would trigger another render.
    const [prevValue, setPrevValue] = useState(value);

    const [prevPropDisabled, setPrevPropDisabled] = useState(disabled);
    const [prevPropRequired, setPrevPropRequired] = useState(required);
    const [prevPropReadOnly, setPrevPropReadOnly] = useState(readOnly);
    const [prevPropId, setPrevPropId] = useState(id);

    const [prevPlaceholderProp, setPrevPlaceholderProp] =
      useState(placeholderProp);
    const [prevOnSuggest, setPrevOnSuggest] = useState(() => onSuggest);

    const isControlled = value !== undefined;
    useEffect(() => {
      setIsControlled(isControlled);
    }, [isControlled, setIsControlled]);

    const placeholder = intl.formatMessage(filterFieldMessages.placeholder);
    const overlayStacking = useContext(OverlayStackingContext);

    // Suggestions overlay setup.
    const suggestionsOverlayRef = useRef<HTMLDivElement | null>(null);
    const {
      targetRef,
      overlayRef: _overlayRef,
      overlayState,
      overlayProps: _overlayProps,
    } = useOverlayWithTrigger({
      placement: 'bottom-start',
      placementFallbacks: ['top-start'],
      widthStrategy: 'trigger',
      offset: 4,
      // Workaround to close all other overlays when opening the suggestions overlay to avoid overlapping overlays.
      // TODO: Refactor with APPDEV-3373.
      onOpenChange(isOpen: boolean) {
        if (
          isOpen &&
          !suggestionsOverlayRef.current &&
          editorViewRef.current?.hasFocus
        ) {
          closeAllOverlays(overlayStacking);

          // Workaround:
          // Closing all other overlays unmounts the focus scopes as well, in turn returning the focus
          // to the closed overlay's trigger (e.g. TimeframeSelector).
          // To keep the focus on the filter field, we need to move it back programmatically.
          // TODO: Refactor with APPDEV-3373.
          requestAnimationFrame(() => {
            editorViewRef.current?.focus();
          });
        }
      },
    });

    const overlayRef = useMergeRefs<HTMLDivElement | null>([
      suggestionsOverlayRef,
      _overlayRef,
    ]);
    const { style: overlayStyles } = _overlayProps;

    // Element refs.
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const editorViewRef = useRef<EditorView | null>(null);
    const internalRootRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useMergeRefs<HTMLDivElement | null>([
      targetRef,
      internalRootRef,
    ]);
    const hiddenInputRef = useRef<HTMLInputElement | null>(null);
    const lastEmittedHadErrorsRef = useRef(false);

    const getFilterFieldValidationState = useCallback(
      (
        trigger: ValidationTrigger,
        currentValue: string = valueRef.current,
      ): ValidationState => {
        const inputId = hiddenInputRef.current?.id
          ? [hiddenInputRef.current.id]
          : [];

        if (hiddenInputRef.current?.validity.customError) {
          return getValidationState(
            StratoValidityStateKeys.syntaxError,
            trigger,
            intl.formatMessage(filterFieldMessages.syntaxError),
            inputId,
          );
        }

        if (requiredRef.current && currentValue === '') {
          return getValidationState(
            NativeValidityStateKeys.valueMissing,
            trigger,
            // `hiddenInputRef.current?.validationMessage` would read the
            // native DOM element, whose `value`/`required` attributes lag
            // behind `currentValue`/`requiredRef` until React's next commit —
            // the same staleness this function's parameters were introduced
            // to avoid. Use the same i18n message source as the displayed
            // required error instead.
            getI18nValidationMessage(
              intl,
              'valueMissing',
              hiddenInputRef.current,
            ),
            inputId,
          );
        }
        return { status: 'done', trigger, errors: [] };
      },
      [hiddenInputRef, intl],
    );

    const emitValidationState = useCustomMessageEmitter(
      _emitValidationState,
      () => getFilterFieldValidationState('manual'),
    );

    const ariaLabelingProps = _useAriaLabelingProps(props);
    const pastingInfoRef = useRef<PastingInfo>({
      pastingInProgress: false,
    });

    // Error boundary awareness
    const { viewDispatch } = useErrorAwareViewRef(editorViewRef);

    // Linting.
    const filterFieldLinter = useFilterFieldLinting(customTypes);

    // Flag for the form validation to indicate whether the FilterField has been blurred.
    const isDirtyRef = useRef<boolean>(false);

    const [prevDisabled, setPrevDisabled] = useState(disabled);

    if (disabled !== prevDisabled) {
      setPrevDisabled(disabled);
      viewDispatch({
        effects: disabledPropCompartment.reconfigure(
          EditorView.editable.of(!disabled),
        ),
      });
    }

    const [prevReadOnly, setPrevReadOnly] = useState(readOnly);

    if (readOnly !== prevReadOnly) {
      setPrevReadOnly(readOnly);
      viewDispatch({
        effects: readOnlyPropCompartment.reconfigure(
          EditorState.readOnly.of(readOnly || false),
        ),
      });
    }

    // FilterField form control message ids
    const filterFieldCustomErrorRef = useRef<string>(useId());
    const filterFieldRequiredErrorRef = useRef<string>(useId());

    const validate = useCallback(
      (currentValue: string = valueRef.current) => {
        // Handle required error
        if (currentValue === '' && requiredRef.current) {
          // Add required error.
          formFieldMessageContextDispatch?.({
            type: FormFieldMessagesAction.ADDMESSAGE,
            payload: {
              id: filterFieldRequiredErrorRef.current,
              error: new ValidationError(
                getI18nValidationMessage(
                  intl,
                  'valueMissing',
                  hiddenInputRef.current,
                ),
                {
                  source: 'native',
                  validityStateKey: NativeValidityStateKeys.valueMissing,
                  formControlId: hiddenInputRef.current?.id,
                },
              ),
            },
          });
        } else {
          // Clean up required error — either the value is non-empty, or the
          // field is no longer required (e.g. the `required` prop changed).
          formFieldMessageContextDispatch?.({
            type: FormFieldMessagesAction.REMOVEMESSAGE,
            payload: {
              id: filterFieldRequiredErrorRef.current,
              source: 'native',
            },
          });
        }
      },
      [formFieldMessageContextDispatch, intl],
    );

    const updateSyntaxError = useCallback(
      (isValid: boolean) => {
        setHasSyntaxError(!isValid);
        hiddenInputRef.current?.setCustomValidity(
          isValid ? '' : intl.formatMessage(filterFieldMessages.syntaxError),
        );

        if (!isValid) {
          formFieldMessageContextDispatch?.({
            type: FormFieldMessagesAction.ADDMESSAGE,
            payload: {
              id: filterFieldCustomErrorRef.current,
              // Emit the semantic 'syntaxError' key (not 'customError') so this
              // direct dispatch and the form runtime's re-injected entry share a
              // validityStateKey.
              error: new ValidationError(
                intl.formatMessage(filterFieldMessages.syntaxError),
                {
                  source: 'strato',
                  validityStateKey: StratoValidityStateKeys.syntaxError,
                  formControlId: hiddenInputRef.current?.id,
                },
              ),
            },
          });
        } else {
          formFieldMessageContextDispatch?.({
            type: FormFieldMessagesAction.REMOVEMESSAGE,
            payload: {
              id: filterFieldCustomErrorRef.current,
              source: 'strato',
            },
          });
        }
      },
      [formFieldMessageContextDispatch, setHasSyntaxError, intl],
    );

    const onInputChange = (currentValue: string = valueRef.current) => {
      if (isDirtyRef.current) {
        validate(currentValue);
        const state = getFilterFieldValidationState('change', currentValue);
        const hasErrors = state.errors.length > 0;

        // Focused = user is interacting → suppress valid→invalid until blur.
        // Not focused = external change (e.g. a controlled value prop update
        // from outside the component) → emit immediately.
        // `editorViewRef.current?.hasFocus` alone is too narrow: clicking the
        // clear/pin suffix buttons moves DOM focus onto the button itself
        // (before it's restored to the editor), which would otherwise look
        // like the field lost focus and defeat the suppression below. Treat
        // focus anywhere inside the component the same as the editor itself
        // being focused, matching the `onBlur` handler's own check.
        const hasFocusWithinComponent =
          editorViewRef.current?.hasFocus ||
          (document.activeElement !== null &&
            !!internalRootRef.current?.contains(document.activeElement));

        if (
          !lastEmittedHadErrorsRef.current &&
          hasErrors &&
          hasFocusWithinComponent
        ) {
          return;
        }
        lastEmittedHadErrorsRef.current = hasErrors;
        emitValidationState(state);
      }
    };

    // Shared blur-time validation used by both the container blur path
    // (via useContainerFocus's `getBlurValidationState`) and the hidden
    // input's `onInvalid` path.
    const performBlurValidation = (): ValidationState => {
      isDirtyRef.current = true;
      validate();
      const state = getFilterFieldValidationState('blur');
      lastEmittedHadErrorsRef.current = state.errors.length > 0;
      return state;
    };

    const onInputBlur = () => {
      const state = performBlurValidation();
      formFieldMessageContextDispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });
      emitValidationState(state);
    };

    const {
      containerClassName,
      onBlur: hookOnBlur,
      ...restContainerFocusProps
    } = useContainerFocus<HTMLDivElement>({
      disabled,
      emitValidationState,
      getBlurValidationState: performBlurValidation,
      onFocus: (event) => {
        // Re-focusing the field resets touched so displayed errors hide
        // while the user re-edits (matches existing FilterField UX).
        formFieldMessageContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: false },
        });
        focusHandlers.onFocus?.(event);
        onFocus?.(event);
      },
      onBlur: (event) => {
        focusHandlers.onBlur?.(event);
        onBlur?.(event);
      },
    });

    // The hook's blur handler calls `hiddenInput.reportValidity()`, which
    // synchronously fires the `invalid` event whenever the field is invalid.
    // Our hidden-input `onInvalid` handler refocuses the field for the
    // form-submit case — but if that refocus runs during a blur, it bounces
    // focus straight back into the container and defeats the blur entirely.
    // We flip this flag while the hook runs so `onInvalid` can distinguish
    // its two triggers and skip refocus during the blur path.
    const isReportingValidityRef = useRef(false);
    const containerFocusProps = {
      ...restContainerFocusProps,
      onBlur: (event: ReactFocusEvent<HTMLDivElement>) => {
        isReportingValidityRef.current = true;
        try {
          hookOnBlur(event);
        } finally {
          isReportingValidityRef.current = false;
        }
      },
    };

    const saveRecentFilter = useRecentFilterMutation(filterNamespace);
    useRecentFilterSuggestions(editorViewRef, filterNamespace);
    usePinnedFilterSuggestions(
      editorViewRef,
      filterNamespace,
      overlayState.isOpen,
    );

    useImperativeHandle(
      forwardedRef,
      (): FilterFieldRef => ({
        element: internalRootRef.current,
        triggerRef: triggerRef.current,
        inputRef: hiddenInputRef.current,
        focus: () => {
          hiddenInputRef.current?.focus();
          // If programmatically focused via the hidden input, we want to move the cursor to the end
          editorViewRef.current?.dispatch({
            selection: { anchor: editorViewRef.current.state.doc.length },
          });
        },
        open: () => hiddenInputRef.current?.focus(),
        close: () => overlayState.close(),
        validate: () => {
          isDirtyRef.current = true;
          validate();
          // Reveal the error immediately, even if focus is still inside the
          // field (e.g. a parent form / wizard triggering validation).
          markTouched();
          const state = getFilterFieldValidationState('manual');
          const validationState = emitValidationState(state);
          lastEmittedHadErrorsRef.current = validationState.errors.length > 0;
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          isDirtyRef.current = true;
          validate();
          markTouched();
          const state = getFilterFieldValidationState('manual');
          const validationState = emitValidationState(state);
          lastEmittedHadErrorsRef.current = validationState.errors.length > 0;
          return validationState;
        },
        saveRecentFilter: async () => {
          if (filterNamespace) {
            await saveRecentFilter(valueRef.current).catch(() => undefined);
          }
        },
      }),
      [
        overlayState,
        validate,
        markTouched,
        filterNamespace,
        saveRecentFilter,
        emitValidationState,
      ],
    );

    // FilterField state.
    const [state, dispatch] = useReducer(filterFieldReducer, {
      suggestions: new Map(),
      activeSuggestion: -1,
    });
    const [hideDeleteWhileTyping, setHideDeleteWhileTyping] = useState(false);

    const {
      ariaAttributes,
      autoSuggestionsConfig,
      recentSuggestionValues,
      pinnedSuggestionValues,
      logicalOperatorSuggestionValues,
      pastedContentSuggestionValues,
      searchSuggestionValues,
      fallbackKeySuggestionValues,
      statementSuggestionValues,
      focusedListItem,
      suggestionsContext,
      applySuggestion,
      handleNextSuggestion,
      _onSuggest,
    } = useSuggestions({
      ariaDisabled,
      autoSuggestions,
      isOpen: overlayState.isOpen,
      id,
      suggestionsOverlayRef,
      editorViewRef,
      onSuggest,
      viewDispatch,
      ariaLabelingProps,
    });

    // When a consumer provides their own <FilterField.Suggestions> slot, the
    // suggestions live in that slot's children rather than in ours.
    const suggestionChildren = isValidElement<{ children?: ReactNode }>(
      suggestionsSlot,
    )
      ? suggestionsSlot.props.children
      : children;

    // Flattened here rather than in <Suggestions> so that the suggestions list and
    // the aria-live announcer share one list. Skipped while the overlay is closed
    // to keep it as lazy as it was when <Suggestions> owned the computation.
    const { flattenedSuggestionItems, groupMetadata } = useMemo(() => {
      if (readOnly || !overlayState.isOpen) {
        return EMPTY_FLATTENED_SUGGESTIONS;
      }

      const groupedSuggestions = autoSuggestions
        ? autoSuggestionsConfig.groupedSuggestions
        : undefined;

      return flattenSuggestions([
        ...Object.values(groupedSuggestions ?? {}).filter(
          (group) => group && (group.suggestions?.length ?? 0) > 0,
        ),
        ...Children.toArray(suggestionChildren),
      ]);
    }, [
      readOnly,
      overlayState.isOpen,
      autoSuggestions,
      autoSuggestionsConfig.groupedSuggestions,
      suggestionChildren,
    ]);

    // Reference of the currently applied value, stored in a ref to avoid constantly
    // reconfiguring the editor.
    const valueRef = useRef(value ?? defaultValue ?? '');
    // Reference of the transaction needed to get from the current state to the next
    // state if the filter field is controlled.
    const transactionRef = useRef<TransactionSpec[] | undefined>(undefined);
    // Reference to determine whether search suggestions are valid for the current input.
    const isValidSearchRef = useRef(false);
    // Reference to read the current `required` prop from the mount-only editor
    // effect below, whose closures would otherwise capture a stale value.
    const requiredRef = useRef(required);
    requiredRef.current = required;

    // Register with an enclosing (or out-of-subtree, via `_form`) `_Form` so the
    // FilterField's value and validity flow into the form's aggregation,
    // apply-gating, and focus-first-invalid. FilterField owns its own validity
    // pipeline, so it hands the runtime custom accessors instead of relying on
    // the hook's native-input defaults.
    const formControl = useInternalFormRegistration({
      id,
      name,
      form: _form,
      messageTarget: _messageTarget,
      onValidityChange,
      getValue: () => valueRef.current,
      focus: () => {
        hiddenInputRef.current?.focus({ focusVisible: true });
        editorViewRef.current?.dispatch({
          selection: { anchor: editorViewRef.current.state.doc.length },
        });
      },
      validateField: ({ reveal, trigger }) => {
        isDirtyRef.current = true;
        validate();
        if (reveal) {
          markTouched();
        }
        const validationState = emitValidationState(
          getFilterFieldValidationState(trigger),
        );
        lastEmittedHadErrorsRef.current = validationState.errors.length > 0;
        return Promise.resolve(validationState);
      },
      resetValidation: () => {
        hiddenInputRef.current?.setCustomValidity('');
        isDirtyRef.current = false;
        lastEmittedHadErrorsRef.current = false;
        formFieldMessageContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: false, resetShowError: true },
        });
        formFieldMessageContextDispatch?.({
          type: FormFieldMessagesAction.REMOVEMESSAGE,
          payload: {
            id: filterFieldRequiredErrorRef.current,
            source: 'native',
          },
        });
        formFieldMessageContextDispatch?.({
          type: FormFieldMessagesAction.REMOVEMESSAGE,
          payload: {
            id: filterFieldCustomErrorRef.current,
            source: 'strato',
          },
        });
      },
    });
    formValidityChangeRef.current = formControl.onValidityChange;

    /**
     * Effect for configuring the editor.
     * Only called on mount.
     * State variables / stateful functions needed are either stored in refs or updated using codemirror compartments.
     */
    useEffect(() => {
      if (!triggerRef.current) {
        return;
      }

      function handleOnChange(transaction: Transaction) {
        if (!editorViewRef.current) {
          return transaction;
        }

        let newValue = transaction.newDoc.toString();
        let transactions: TransactionSpec[] = [];

        const tree = syntaxTree(transaction.state);

        const {
          tree: convertedTree,
          isValid,
          isValidSearch,
        } = getSyntaxTree({
          tree,
          view: editorViewRef.current,
          numberParser,
          doc: transaction.newDoc.toString(),
          internalValidatorMap: internalValidatorMapRef.current,
          customTypes: customTypesRef.current,
          parserConfig,
        });

        isValidSearchRef.current = isValidSearch;

        // The required (valueMissing) and syntax (customError) errors are mutually
        // exclusive: an empty value is always syntactically valid, and a syntax
        // error can only occur for a non-empty value. Whichever error newly applies
        // after this change must be dispatched before the other (now-stale) one is
        // removed — otherwise the message list would transiently report zero
        // errors, which forces the shared showError flag to false, a state it can
        // then only leave via the next blur.
        // this has to run regardless of whether the newValue is equal to the
        // valueRef.current as we also want to update the error state when
        // programmatically changing the value
        if (newValue === '') {
          validate(newValue);
          updateSyntaxError(isValid);
        } else {
          updateSyntaxError(isValid);
          validate(newValue);
        }

        const annotation = transaction.annotation(stringAnnotation);
        if (annotation === ANNOTATION_DOC_CHANGED_CONTROLLED) {
          onInputChange(newValue);
          return transaction;
        }

        onInputChange(newValue);

        // Avoid overriding the action for logging errors for suggestion transactions.
        if (annotation !== ANNOTATION_SUGGESTION_APPLY) {
          setAction({
            type: 'handleOnChange',
            data: {
              value: valueRef.current,
              newValue,
            },
          });
        }

        if (newValue !== valueRef.current) {
          if (isControlled) {
            handleControlledPasting(transaction, pastingInfoRef);
          }

          setHideDeleteWhileTyping(true);

          // Run auto-formatter for any modifications of the document other than deletions.
          // If something is deleted, the auto-formatter has already been executed when the
          // new document was entered.
          if (
            !transaction.isUserEvent('delete.backward') &&
            !transaction.isUserEvent('delete.forward')
          ) {
            [newValue, transactions] = autoFormatLogicalOperators(
              newValue,
              tree,
              transaction.selection?.ranges[
                transaction.selection?.mainIndex ?? 0
              ]?.to ?? transaction.newDoc.toString().length,
            );
          }

          onChangeRef.current?.(newValue, convertedTree, isValid);

          transactionRef.current = [{ selection: transaction.newSelection }];
          const userEvent = transaction.annotation(Transaction.userEvent);
          transactionRef.current = [
            {
              ...transaction,
              // On Windows / Linux, hitting 'Shift + Delete' results in an undefined selection,
              // leading to the suggestions callback not being triggered.
              // Even for an empty value, there should always be a selection.
              selection: transaction.newSelection ?? { anchor: 0 },
              // Annotations are not automatically stored, we need to do that manually
              // to be able to determine, which action triggered the transaction when applying
              // the new value in a controlled scenario.
              annotations: Transaction.userEvent.of(
                userEvent ?? transaction.annotation(stringAnnotation) ?? '',
              ),
            },
            ...transactions,
          ];

          if (!isControlled) {
            valueRef.current = newValue;
            onInputChange();
          }

          return isControlled ? [] : [transaction, ...transactions];
        }

        return [transaction];
      }

      internalValidatorMapRef.current = toInternalValidatorMap(validatorMap);

      const initialEditorState = EditorState.create({
        doc: value ?? defaultValue ?? '',
        extensions: [
          FilterFieldLanguageExtension,
          filterFieldLinter,
          createFilterFieldSyntaxHighlightingExtension(
            // oxlint-disable-next-line typescript/no-deprecated
            !!parserConfig?.matchesWildcardConversion,
          ),
          FilterFieldInteractionsExtension({
            closeSuggestions: overlayState.close,
            showBoundary,
          }),
          DynamicFilterFieldConfigurations({
            ariaAttributes,
            disabled: !!disabled,
            readOnly,
            placeholder: placeholderProp ?? placeholder,
          }),
          EditorView.domEventObservers({
            compositionend: () => {
              isUserEventComposeRef.current = false;
              editorViewRef.current?.dispatch({
                annotations: stringAnnotation.of(ANNOTATION_COMPOSE_END),
              });
            },
          }),
          FilterFieldRecentSavedFiltersExtension,

          /**
           * Suggestions
           */
          suggestionsCompartment.of(
            suggestionsExtension({
              overlayState,
              numberParser,
              onSuggest,
              internalValidatorMap: internalValidatorMapRef.current,
              _onSuggest,
              getSyntaxTree,
              parserConfig,
              customTypes: customTypesRef.current,
            }),
          ),

          /**
           * Listeners
           */
          // Update cursor position for error logging whenever it changes.
          EditorState.transactionFilter.of((transaction) => {
            const userEvent = transaction.annotation(Transaction.userEvent);
            if (userEvent?.includes('select')) {
              setCursorPosition(transaction.state.selection.main.anchor);
            }

            return transaction;
          }),

          // Intercept transactions that change the doc to not instantly apply the changes
          // in a controlled scenario. Instead, the transaction is stored so we can apply
          // it if the consumer actually updates the value.
          EditorState.transactionFilter.of((transaction) => {
            // Safeguard.
            if (!editorViewRef.current) {
              return transaction;
            }

            // Check annotations for a controlled value update.
            const annotation = transaction.annotation(stringAnnotation);
            if (annotation === ANNOTATION_DOC_CHANGED_CONTROLLED) {
              return handleOnChange(transaction);
            }

            // We need to trigger startCompletion when pasting mode is ending to update the suggestions.
            if (annotation === ANNOTATION_END_PASTING_MODE) {
              queueMicrotask(() => {
                try {
                  if (editorViewRef?.current) {
                    startCompletion(editorViewRef.current);
                  }
                } catch (e) {
                  showBoundary(e);
                }
              });
            }

            // If we have a user event (usually some kind of input), we need to check for
            // composition events to properly handle composed characters in a controlled scenario.
            let userEvent = transaction.annotation(Transaction.userEvent);

            // Certain custom transactions should also be treated as user event.
            if (userEvent === undefined) {
              switch (annotation) {
                case ANNOTATION_COMPOSE_END:
                  userEvent = ANNOTATION_COMPOSE_END;
                  break;
                case ANNOTATION_DELETE_STATEMENT:
                  userEvent = ANNOTATION_DELETE_STATEMENT;
                  break;
                case ANNOTATION_SUGGESTION_APPLY: {
                  userEvent = ANNOTATION_SUGGESTION_APPLY;

                  // If there is a selection of the value placeholder of a wildcard
                  // suggestion we need to save the selection to be able to restore
                  // it after a controlled value update
                  const selection = transaction.selection?.ranges;
                  if (
                    selection &&
                    selection[0] &&
                    selection[0].to - selection[0].from === 5
                  ) {
                    valuePlaceholderWildcardSelection.current = {
                      isActive: true,
                      range: { from: selection[0].from, to: selection[0].to },
                      potentialNewValue: transaction.newDoc.toString(),
                    };
                  }
                  break;
                }
              }
            }

            if (userEvent === 'input.type.compose.start') {
              // Composition event was started. Do not intercept any transaction while ongoing.
              isUserEventComposeRef.current = true;
            }

            // Any transaction not related to a user event should not be handled specifically.
            // Transactions of an ongoing composition should also be forwarded to properly
            // show the composition steps in the input.
            if (!userEvent || isUserEventComposeRef.current) {
              return transaction;
            }
            if (
              ((transaction.docChanged && userEvent) ||
                !isUserEventComposeRef.current) &&
              // We add the space character in the updateListener, so we don't want to call OnChange.
              !transaction.isUserEvent('select')
            ) {
              return handleOnChange(transaction);
            }

            return transaction;
          }),

          EditorView.updateListener.of((update) => {
            // Check if the transaction is undo or redo in order to not accidentally call onChange
            // twice. Usually onChange is called before applying the changes. However, undo and redo
            // are considered as transactions that occur after the view has been updated.
            // Therefore, we explicitly call onChange if that is the case.
            const isUndoOrRedo = update.transactions.some(
              (transaction) =>
                transaction.isUserEvent('undo') ||
                transaction.isUserEvent('redo'),
            );

            if (update.docChanged && isUndoOrRedo) {
              update.transactions.forEach((transaction) =>
                handleOnChange(transaction),
              );

              editorView?.dispatch({
                effects: [
                  pastingEffect.of({
                    pastingInProgress: false,
                  }),
                ],
              });
            }
          }),

          // Update Listener for pasting
          EditorView.updateListener.of((update) => {
            handlePastingTransaction(
              update,
              pastingInfoRef,
              editorView,
              editorViewRef,
            );
          }),

          // Automatically insert a space at the end of the input when entering edit mode,
          // if the cursor is at the end, the statement is complete, and no trailing space or bracket is present.
          // This starts a new statement.
          EditorView.updateListener.of((update) => {
            handleEditMode(update, suggestionsContext);
          }),
        ],
      });

      const editorView = new EditorView({
        state: initialEditorState,
        parent: triggerRef.current,
      });
      wrapEditorCoordsMethods(editorView, {
        onUnrecoverable: showBoundary,
        onRecovered: (error) => {
          logger?.warn(
            'Recovered from a CodeMirror measurement error by clamping ranges.',
            error,
          );
        },
      });
      editorViewRef.current = editorView;

      if (validatorMap) {
        // do not use view ref here
        editorView.dispatch({
          effects: [validatorMapEffect.of(validatorMapRef?.current)],
          // In order to re-run the decorations and re-generate errors and diagnostics,
          // we have to dispatch a new document.
          changes: {
            from: 0,
            to: editorViewRef.current.state.doc.length,
            insert: editorViewRef.current.state.doc,
          },
        });
      }

      const { isValid, isValidSearch } = getSyntaxTree({
        tree: syntaxTree(editorView.state),
        view: editorView,
        numberParser,
        doc: editorView.state.doc.toString(),
        internalValidatorMap: internalValidatorMapRef.current,
        customTypes: customTypesRef.current,
        parserConfig,
      });

      isValidSearchRef.current = isValidSearch;
      updateSyntaxError(isValid);

      return () => editorView.destroy();
      // Only run for initializing the filter field.
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update the tree and error state if the mode changes.
    const validatorMapChanged = !isEqual(validatorMap, validatorMapRef.current);
    const customTypesChanged = !isEqual(customTypes, customTypesRef.current);

    if ((validatorMapChanged || customTypesChanged) && editorViewRef.current) {
      if (validatorMapChanged) {
        validatorMapRef.current = validatorMap;
        internalValidatorMapRef.current = validatorMap
          ? toInternalValidatorMap(validatorMap)
          : undefined;
      }

      if (customTypesChanged) {
        customTypesRef.current = customTypes;
      }

      if (validatorMapChanged || customTypesChanged) {
        viewDispatch({
          effects: suggestionsCompartment.reconfigure(
            suggestionsExtension({
              overlayState,
              numberParser,
              onSuggest,
              _onSuggest,
              getSyntaxTree,
              internalValidatorMap: internalValidatorMapRef.current,
              customTypes: customTypesRef.current,
              parserConfig,
            }),
          ),
        });
      }

      queueMicrotask(() => {
        try {
          if (editorViewRef.current) {
            const change = computeMinimalDocChange(
              editorViewRef.current.state.doc.toString(),
              valueRef.current,
            );
            viewDispatch({
              effects: [validatorMapEffect.of(validatorMapRef?.current)],
              changes: change,
            });

            const doc = editorViewRef.current.state.doc;
            const tree = syntaxTree(editorViewRef.current.state);
            const { tree: convertedTree, isValid } = getSyntaxTree({
              tree,
              view: editorViewRef.current,
              numberParser,
              doc: doc.toString(),
              internalValidatorMap: internalValidatorMapRef.current,
              customTypes: customTypesRef.current,
              parserConfig,
            });

            updateSyntaxError(isValid);
            onChangeRef.current?.(doc.toString(), convertedTree, isValid);
            onInputChange();

            // With changed validator map or custom types, the suggestions also need to be re-calculated.
            // Use a nested queueMicrotask to ensure the viewDispatch above is fully applied
            // before triggering completion with the new extension.
            if (validatorMapChanged || customTypesChanged) {
              queueMicrotask(() => {
                try {
                  if (editorViewRef.current) {
                    startCompletion(editorViewRef.current);
                  }
                } catch (e) {
                  showBoundary(e);
                }
              });
            }
          }
        } catch (e) {
          showBoundary(e);
        }
      });
    }

    // Update configuration compartments that are depending on props when the props change.
    if (placeholderProp !== prevPlaceholderProp) {
      viewDispatch({
        effects: placeholderCompartment.reconfigure(
          placeholderExtension(placeholderProp ?? placeholder),
        ),
      });

      setPrevPlaceholderProp(placeholderProp);
    }

    // Re-configure the suggestions extension to use a potentially changed callback.
    if (onSuggest !== prevOnSuggest) {
      viewDispatch({
        effects: suggestionsCompartment.reconfigure(
          suggestionsExtension({
            overlayState,
            numberParser,
            onSuggest,
            _onSuggest,
            getSyntaxTree,
            internalValidatorMap: internalValidatorMapRef.current,
            customTypes: customTypesRef.current,
            parserConfig,
          }),
        ),
      });

      setPrevOnSuggest(() => onSuggest);
    }

    // Dispatch a transaction to update the editor's doc to match the prop
    // value if it changes and the filter field is controlled.
    // Since the value may have been adjusted in the `onChange` callback, we replace the whole document.
    // FIXME: the cursor position jumps in case the value's length changes in the controlled state update.
    if (value !== prevValue && isControlled) {
      setPrevValue(value);
      valueRef.current = value;

      // Wait for any previous codemirror transactions to go through, else it will
      // throw an error that we are trying to update a state that doesn't start
      // from the previous state.
      queueMicrotask(() => {
        try {
          setAction({
            type: 'controlledValueChange',
            data: {
              value: prevValue ?? '',
              newValue: valueRef.current,
            },
          });

          onInputChange();
          const lastTransaction =
            transactionRef.current &&
            transactionRef.current[transactionRef.current.length - 1];
          const selection =
            lastTransaction?.selection ??
            // Fall back to the current editor selection in case the last transaction is undefined
            // to avoid a jumping cursor when typing really fast.
            editorViewRef.current?.state.selection;

          const anchor = Math.min(
            value.length,
            (isEditorSelection(selection)
              ? selection?.ranges[0]?.anchor
              : selection?.anchor) ?? value.length,
          );

          let dispatchedSelection: { anchor: number; head?: number } = {
            anchor,
          };

          // Select the value placeholder if inserting a wildcard suggestion with a placeholder
          if (
            valuePlaceholderWildcardSelection.current?.isActive &&
            valuePlaceholderWildcardSelection.current?.potentialNewValue ===
              value
          ) {
            const selectionFrom =
              valuePlaceholderWildcardSelection.current.range?.from;
            const selectionTo =
              valuePlaceholderWildcardSelection.current.range?.to;

            if (
              selectionFrom &&
              selectionFrom <= value.length &&
              selectionTo &&
              selectionTo <= value.length
            ) {
              dispatchedSelection = {
                anchor: selectionFrom,
                head: selectionTo,
              };
            }
          }

          // Replace only the range that actually changed instead of the whole
          // document. A `from: 0` replacement rebuilds the content DOM and desyncs
          // the contentEditable caret from the editor state.
          const change = computeMinimalDocChange(
            editorViewRef.current?.state.doc.toString() ?? '',
            value,
          );

          viewDispatch({
            changes: change,
            annotations: stringAnnotation.of(ANNOTATION_DOC_CHANGED_CONTROLLED),
            selection: dispatchedSelection,
          });

          // Reset the value placeholder selection.
          if (valuePlaceholderWildcardSelection.current) {
            valuePlaceholderWildcardSelection.current = {
              isActive: false,
            };
          }

          transactionRef.current = undefined;
        } catch (e) {
          showBoundary(e);
        }
      });
    }

    if (
      disabled !== prevPropDisabled ||
      required !== prevPropRequired ||
      readOnly !== prevPropReadOnly ||
      id !== prevPropId
    ) {
      setPrevPropDisabled(disabled);
      setPrevPropRequired(required);
      setPrevPropReadOnly(readOnly);
      setPrevPropId(id);

      // Update disabled if disabled prop changes
      if (disabled !== prevPropDisabled) {
        viewDispatch({
          effects: disabledPropCompartment.reconfigure(
            EditorView.editable.of(!disabled),
          ),
        });
      }

      // Update read-only if read-only prop changes
      if (readOnly !== prevPropReadOnly) {
        viewDispatch({
          effects: readOnlyPropCompartment.reconfigure(
            EditorState.readOnly.of(readOnly),
          ),
        });
      }

      // Update required if required prop changes
      if (required !== prevPropRequired) {
        validate();
      }
    }

    const [hasAriaLivePolite, setHasAriaLivePolite] = useState(false);

    useEffect(() => {
      setHasAriaLivePolite(false);
      const debounceTimer = setTimeout(() => {
        const hasFocusWithinComponent =
          editorViewRef.current?.hasFocus ||
          (document.activeElement !== null &&
            !!internalRootRef.current?.contains(document.activeElement));
        if (!hasFocusWithinComponent) {
          setHasAriaLivePolite(true);
        }
      }, 300);

      return () => {
        clearTimeout(debounceTimer);
      };
    }, []);

    const defaultSuggestionsCount = useMemo(() => {
      if (typeof _defaultSuggestionsCount === 'number') {
        return _defaultSuggestionsCount;
      }
      return {
        empty:
          _defaultSuggestionsCount?.empty ?? DEFAULT_SUGGESTIONS_COUNT.empty,
        filled:
          _defaultSuggestionsCount?.filled ?? DEFAULT_SUGGESTIONS_COUNT.filled,
      };
    }, [_defaultSuggestionsCount]);

    const filterFieldContextValue = useMemo<FilterFieldContextProps>(
      () => ({
        hasAriaLivePolite,
        applySuggestion,
        dispatch,
        focusedListItem,
        filterFieldId: id,
        filterFieldRootId: rootId,
        term: suggestionsContext.current?.token.textValue,
        autoSuggestions: autoSuggestions ? autoSuggestionsConfig : {},
        flattenedSuggestionItems,
        groupMetadata,
        recentSuggestionValues,
        pinnedSuggestionValues,
        logicalOperatorSuggestionValues,
        pastedContentSuggestionValues,
        searchSuggestionValues,
        fallbackKeySuggestionValues,
        fallbackKeys: internalValidatorMapRef.current?.fallbackKeys,
        statementSuggestionValues,
        filterStatementKeysMap:
          internalValidatorMapRef.current?.filterStatementKeysMap,
        triggerRef: triggerRef.current ?? null,
        suggestionsOverlayRef: suggestionsOverlayRef.current ?? null,
        filterNamespace: filterNamespace ?? null,
        editorView: editorViewRef.current,
        parserConfig,
        isValidSearch: isValidSearchRef,
        defaultSuggestionsCount,
        handleNextSuggestion,
      }),
      [
        hasAriaLivePolite,
        applySuggestion,
        focusedListItem,
        id,
        rootId,
        suggestionsContext,
        autoSuggestions,
        autoSuggestionsConfig,
        flattenedSuggestionItems,
        groupMetadata,
        recentSuggestionValues,
        pinnedSuggestionValues,
        logicalOperatorSuggestionValues,
        pastedContentSuggestionValues,
        searchSuggestionValues,
        fallbackKeySuggestionValues,
        internalValidatorMapRef,
        statementSuggestionValues,
        filterNamespace,
        parserConfig,
        defaultSuggestionsCount,
        handleNextSuggestion,
      ],
    );

    return (
      <FilterFieldContext.Provider value={filterFieldContextValue}>
        <Flex
          ref={rootRef}
          id={rootId}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          gap={4}
          flexDirection="column"
          className={clsx(
            styles.wrapper,
            consumerClassName,
            containerClassName,
          )}
          style={consumerStyle}
          onMouseMove={() => {
            setHideDeleteWhileTyping(false);
          }}
          {...containerFocusProps}
          {...behavioralTrackingProps}
          data-dt-component={COMPONENT_NAME}
        >
          <div
            {...inputProps}
            className={clsx(
              editorStyles.editorContainer({
                size: 'default',
                fullHeight: false,
                // check if the editor is disabled first to apply proper styling
                hasError: !(disabled || ariaDisabled) && showError,
                lineNumbers: 'hidden',
                readOnly: disabled || readOnly,
              }),
              styles.container,
              props.className,
              {
                [styles.errorText]: showError,
                [styles.errorVars]: showError,
                [styles.disabled]: disabled,
                [styles.readOnly]: readOnly,
                [styles.ariaDisabled]: ariaDisabled,
                hideDeleteWhileTyping,
              },
            )}
            onKeyDown={(event: ReactKeyboardEvent) => {
              // Let suffix buttons (clear, pin) handle their own Enter/Space
              // activation. Otherwise the Enter branch below calls preventDefault
              // and the focused button never receives a click.
              if (
                event.target instanceof HTMLElement &&
                event.target.closest('button')
              ) {
                return;
              }
              // If readOnly is set, only allow the tab key to be pressed.
              // This moves the focus from within the editor to the next focusable element.
              // Also check if CTRL is pressed to still allow shortcuts like CTRL+'C'.
              if (readOnly && event.key !== 'Tab' && !event.ctrlKey) {
                event.preventDefault();
                return;
              }
              switch (event.key) {
                case 'Space':
                  if (!editorViewRef.current) {
                    return;
                  }

                  if (event.ctrlKey || event.altKey) {
                    overlayState.open();
                    startCompletion(editorViewRef.current);
                  }
                  break;
                case 'Enter': {
                  event.preventDefault();

                  if (event.shiftKey || !editorViewRef.current) {
                    return;
                  }

                  // Submitting the filter field
                  if (!focusedListItem || event.ctrlKey || event.metaKey) {
                    validate();

                    const { tree, isValid } = getSyntaxTree({
                      tree: syntaxTree(editorViewRef.current.state),
                      view: editorViewRef.current,
                      numberParser,
                      doc: editorViewRef.current.state.doc.toString(),
                      internalValidatorMap: internalValidatorMapRef.current,
                      customTypes,
                      parserConfig,
                    });

                    onFilterRef.current?.({
                      value: valueRef.current,
                      syntaxTree: tree,
                      isValid,
                    });

                    overlayState.close();

                    // The native submit is only the fallback for a field with
                    // no `onFilter` handler, so a lone field behaves like a
                    // lone text input. With `onFilter` set that callback is the
                    // only notification; submitting as well would report one
                    // keystroke twice.
                    if (
                      isValid &&
                      hiddenInputRef.current?.form &&
                      !hasFilterHandlerRef.current
                    ) {
                      let inputCount = 0;
                      for (const element of Array.from(
                        hiddenInputRef.current.form.elements,
                      )) {
                        // Check if the filter field is the only visible form control.
                        // Elements included are listed here: https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/elements#value
                        if (
                          !element.hasAttribute('hidden') &&
                          ((element.tagName === 'INPUT' &&
                            element.getAttribute('type') !== 'hidden') ||
                            element.tagName === 'SELECT' ||
                            element.tagName === 'TEXTAREA')
                        ) {
                          inputCount += 1;

                          if (inputCount > 1) {
                            break;
                          }
                        }
                      }

                      if (inputCount === 1) {
                        hiddenInputRef.current?.form?.requestSubmit();
                      }
                    } else if (!isValid) {
                      // We have to wait for the form field to set the validity of the form control.
                      // Then reporting the validity triggers the 'invalid' event, which can be captured on the form.
                      setTimeout(() => {
                        hiddenInputRef.current?.form?.reportValidity();
                      });
                    }
                  } else if (focusedListItem && focusedListItem.id) {
                    // Focusable elements do not have to contain suggestions alone. Suggestions cannot be
                    // invoked via the click logic in the else branch, since they run `onPointerDown`.
                    // See suggestions component.
                    if (state.suggestions.has(focusedListItem.id)) {
                      state.suggestions.get(focusedListItem.id)?.();
                    } else {
                      // Invoke the click of the currently focused element.
                      const focusedElement =
                        document.querySelector<HTMLButtonElement>(
                          `[data-strato-filter-field-virtual-focus-id="${focusedListItem?.id}"]`,
                        );
                      focusedElement?.click();
                    }
                    return;
                  }

                  break;
                }
              }

              // FIXME: the suggestions overlay may be empty, then we shouldn't handle the keys.
              if (overlayState.isOpen) {
                if (overrideKeys.includes(event.key)) {
                  event.preventDefault();
                  event.stopPropagation();
                }

                if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                  const direction =
                    event.key === 'ArrowUp' ? 'previous' : 'next';

                  const overlay = suggestionsOverlayRef.current;
                  const currentlyFocusedItem = getCurrentFocusableListItem(
                    overlay,
                    focusedListItem?.id,
                  );
                  const isAtEnd =
                    currentlyFocusedItem ===
                      getLastFocusableListItem(overlay) && direction === 'next';
                  const isAtStart =
                    currentlyFocusedItem ===
                      getFirstFocusableListItem(overlay) &&
                    direction === 'previous';

                  if (isAtEnd || isAtStart) {
                    const scrollContainer = overlay?.querySelector<HTMLElement>(
                      '.strato-filter-field-suggestions',
                    );
                    if (scrollContainer) {
                      scrollContainer.scroll({
                        top:
                          direction === 'next'
                            ? 0
                            : scrollContainer.scrollHeight,
                      });
                      requestAnimationFrame(() => {
                        const nextSuggestion = getNextFocusableListItem(
                          direction,
                          overlay,
                          focusedListItem?.id,
                        );
                        handleNextSuggestion(nextSuggestion);
                      });
                    }
                    return;
                  }

                  const nextSuggestion = getNextFocusableListItem(
                    direction,
                    overlay,
                    focusedListItem?.id,
                  );
                  handleNextSuggestion(nextSuggestion);
                }
              }
            }}
            onKeyDownCapture={(event: ReactKeyboardEvent) => {
              if (
                (event.key === 'Home' ||
                  event.key === 'End' ||
                  event.key === 'PageUp' ||
                  event.key === 'PageDown') &&
                focusedListItem
              ) {
                event.stopPropagation();
                event.preventDefault();

                let nextSuggestion: HTMLElement | null = null;

                if (event.key === 'PageUp' || event.key === 'PageDown') {
                  const direction =
                    event.key === 'PageUp' ? 'previous' : 'next';

                  nextSuggestion = getNextFocusableListItem(
                    direction,
                    suggestionsOverlayRef.current,
                    focusedListItem?.id,
                    5,
                  );
                  handleNextSuggestion(nextSuggestion);
                } else if (event.key === 'Home' || event.key === 'End') {
                  const overlay = suggestionsOverlayRef.current;
                  const scrollContainer = overlay?.querySelector<HTMLElement>(
                    '.strato-filter-field-suggestions',
                  );

                  if (scrollContainer) {
                    scrollContainer.scroll({
                      top:
                        event.key === 'Home' ? 0 : scrollContainer.scrollHeight,
                    });
                  }

                  requestAnimationFrame(() => {
                    const nextSuggestion =
                      event.key === 'Home'
                        ? getFirstFocusableListItem(overlay)
                        : getLastFocusableListItem(overlay);
                    handleNextSuggestion(nextSuggestion);
                  });
                }
              }
            }}
            onMouseDown={(event) => {
              if (disabled) {
                event.preventDefault();
              }
            }}
          >
            <span
              className={styles.filterIcon({
                hasError: showError,
                disabled: disabled || Boolean(ariaDisabled),
              })}
            >
              <FilterIcon />
            </span>
            <div ref={triggerRef} style={{ maxHeight: 'inherit' }} />
            <FilterFieldSuffixButtons
              value={valueRef.current}
              ariaDisabled={ariaDisabled}
              disabled={disabled}
              showError={showError}
              readOnly={readOnly}
            />
          </div>
          <AnimatePresence>
            {/* The editor can still be focused if readOnly is set. */}
            {/* Since the content is not editable, we prevent the overlay from opening. */}
            {!readOnly && overlayState.isOpen && (
              <SuggestionsOverlay
                ref={overlayRef}
                data-testid={
                  dataTestId
                    ? `filterfield-suggestions-${dataTestId}`
                    : undefined
                }
                overlayProps={{ style: overlayStyles }}
                data-dtrum-mask={dataDtrumMask}
                data-dtrum-allow={dataDtrumAllow}
              >
                {suggestionsSlot === undefined ? (
                  <Suggestions>{children}</Suggestions>
                ) : (
                  children
                )}
              </SuggestionsOverlay>
            )}
          </AnimatePresence>
          {hint && (
            <BaseItem
              variant={showError ? 'error' : 'hint'}
              disabled={disabled || ariaDisabled}
              {...hintProps}
            >
              {hint}
            </BaseItem>
          )}
          <VisuallyHidden>
            <input
              ref={hiddenInputRef}
              name={name}
              form={_form}
              value={valueRef.current}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              tabIndex={-1}
              id={id}
              onFocus={() => {
                // The focus should move to the FilterField when clicking on
                // the label (when using it in a FormField)
                editorViewRef.current?.focus();
              }}
              onChange={() => {
                /** noop */
              }}
              onInvalid={(event) => {
                event.preventDefault();
                markTouched();
                // Skip the refocus + explicit blur validation when this
                // `invalid` fires from the container blur path — the hook is
                // already running that logic and refocusing would bounce
                // focus back into the field.
                if (!isReportingValidityRef.current) {
                  event.currentTarget.focus();
                  onInputBlur();
                }
              }}
              aria-hidden="true"
            />
          </VisuallyHidden>
          <VisuallyHidden>
            <div role="status">
              {hasAriaLivePolite
                ? valueRef.current ||
                  intl.formatMessage(
                    filterFieldMessages.suggestions.ariaLiveInfo.filterCleared,
                  )
                : ''}
            </div>
          </VisuallyHidden>
        </Flex>
      </FilterFieldContext.Provider>
    );
  },
);

(_FilterField as typeof FilterField & { displayName: string }).displayName =
  COMPONENT_NAME;

/**
 * FilterField component that uses a fallback formFieldMessages-context if it wasn't wrapped in a FormField.
 * @internal
 */
const InternalFilterField = withFormFieldMessageContextProvider<
  FilterFieldProps,
  FormControlWithOverlayRef,
  HTMLDivElement
>(
  forwardRef<FilterFieldRef, FilterFieldProps>((props, forwardedRef) => {
    const stratoLogger = useMemo(
      () =>
        createStratoLogger.bind({
          name: props.name,
          id: props.id,
        })(COMPONENT_NAME),
      [props.name, props.id],
    );

    const cursorPosition = useErrorLogsStore((state) => state.cursorPosition);
    const action = useErrorLogsStore((state) => state.action);
    const isControlled = useErrorLogsStore((state) => state.isControlled);

    const loggerContext = useMemo(
      () => ({ logger: stratoLogger }),
      [stratoLogger],
    );

    return (
      <ErrorBoundary
        onError={(error) => {
          if (action !== null) {
            action.data.value = obfuscateFilterString(
              action.data.value,
              props.validatorMap,
              props.parserConfig,
            );

            if (
              action.type === 'handleOnChange' ||
              action.type === 'controlledValueChange'
            ) {
              action.data.newValue = obfuscateFilterString(
                action.data.newValue,
                props.validatorMap,
                props.parserConfig,
              );
            }
          }

          stratoLogger.error(error?.message, error, {
            filterString: obfuscateFilterString(
              props.value ?? props.defaultValue ?? '',
              props.validatorMap,
              props.parserConfig,
            ),
            cursorPosition,
            action,
            isControlled,
          });
        }}
        FallbackComponent={(fallbackProps) => (
          <ErrorFallback {...fallbackProps} />
        )}
      >
        <FilterFieldLoggerContext.Provider value={loggerContext}>
          <_FilterField {...props} ref={forwardedRef} />
        </FilterFieldLoggerContext.Provider>
      </ErrorBoundary>
    );
  }),
);

/**
 `FilterField` is an advanced, text-based filtering component. It supports complex data filtering with intuitive filter field syntax and auto-suggestions.
 * @public
 */
export const FilterField = Object.assign(InternalFilterField, {
  Suggestions,
  Suggestion,
  SuggestionGroup,
  SuggestionGroupLabel,
  SuggestionDetails,
});
